import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { queryClient } from "~/backend/queryClient"
import { useRemoveCustomFieldProof } from "~/gen/api"
import { CustomFieldValue } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldInput"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type DocumentStateUpdater = {
  setValue: Dispatch<SetStateAction<CustomFieldValue>>
  setCustomFieldValues: Dispatch<
    SetStateAction<TraceabilityResponses.CustomFieldValue[]>
  >
}

export type RemoveCustomFieldProofHookProps = {
  customFieldValueId: string
  operationId: number
  inputOpen: () => void
  stateUpdaters: DocumentStateUpdater
}

export const useRemoveCustomFieldProofHook = ({
  customFieldValueId,
  operationId,
  inputOpen,
  stateUpdaters,
}: RemoveCustomFieldProofHookProps) => {
  const { t } = useTranslation("traceability")

  const { mutate: removeCustomFieldProof, isPending: isRemoving } =
    useRemoveCustomFieldProof({
      mutation: {
        onSuccess: async () => {
          stateUpdaters.setValue((prev) => ({
            ...prev,
            selectedFile: null,
            document: null,
          }))
          stateUpdaters.setCustomFieldValues((prev) => {
            return prev.map((field) => {
              if (field.id === customFieldValueId) {
                const updated = {
                  ...field,
                  document: null,
                }
                return updated
              }
              return field
            })
          })

          inputOpen()
          showSuccessNotification({
            message: t(
              "customFields.updateCustomField.customFieldProof.message"
            ),
            title: t("customFields.updateCustomField.customFieldProof.title"),
          })
          await queryClient.invalidateQueries({
            queryKey: ["listTraceabilityRequests", operationId],
            refetchType: "none",
          })
        },
        onError: async (error) => {
          showAxiosError({
            showNotification: true,
            error,
          })
          await queryClient.invalidateQueries({
            queryKey: ["listTraceabilityRequests", operationId],
          })
        },
      },
    })

  return {
    removeCustomFieldProof,
    isRemoving,
  }
}
